import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
const TITLE = "حاسبة العمر - احسب عمرك بالهجري والميلادي بدقة";
const DESCRIPTION =
  "احسب عمرك بدقة بالتقويم الهجري أو الميلادي. حاسبة عمر إلكترونية دقيقة تعرض عمرك بالسنوات والأشهر والأيام والساعات، مع معلومات إضافية عن يوم ميلادك والأيام المتبقية لعيد ميلادك القادم";
const KEYWORDS =
  "حساب العمر, احسب عمرك بالهجري, احسب عمرك بالميلادي, حاسبة العمر, كم عمري, العمر بالميلادي, حساب العمر بالدقائق, حساب العمر بالأيام, تحويل العمر, العمر الميلادي, حساب عمر الشخص, حاسبة العمر الدقيقة";
const OG_URL = "https://do-calculate.com/calculator/ar/agegregorian/";
// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة العمر بالميلادي",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "جميع الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة العمر بالميلادي",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو العمر الميلادي؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "العمر الميلادي هو الطريقة التي نستخدمها لقياس عمر الشخص بناءً على تاريخ ميلاده وفقاً للتقويم الميلادي الشهري والسنوي.",
        },
      },
      {
        "@type": "Question",
        name: "كيف يمكنني حساب العمر الميلادي؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يمكنك حساب العمر الميلادي بإدخال تاريخ ميلادك في الحاسبة، وستقوم تلقائياً بحساب عمرك بالسنوات والأشهر والأيام والساعات بدقة تامة.",
        },
      },
      {
        "@type": "Question",
        name: "ما هي استخدامات حساب العمر الميلادي؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يُستخدم حساب العمر الميلادي في التأمل في تجربة الحياة، تخطيط المستقبل، تحديد الأهداف الشخصية، وفي مجالات التأمين والتقاعد والرعاية الصحية.",
        },
      },
    ],
  },
];
const DAYS_OF_WEEK = [
  "الأحد",
  "الاثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
  "الجمعة",
  "السبت",
];
const GREGORIAN_MONTHS = [
  "كانون الثاني",
  "شباط",
  "آذار",
  "نيسان",
  "أيار",
  "حزيران",
  "تموز",
  "آب",
  "أيلول",
  "تشرين الأول",
  "تشرين الثاني",
  "كانون الأول",
];
const HIJRI_MONTHS = [
  "محرم",
  "صفر",
  "ربيع الأول",
  "ربيع الثاني",
  "جمادى الأولى",
  "جمادى الآخرة",
  "رجب",
  "شعبان",
  "رمضان",
  "شوال",
  "ذو القعدة",
  "ذو الحجة",
];
const SEASONS = [
  {
    name: "الشتاء",
    start: { month: 12, day: 21 },
    end: { month: 3, day: 20 },
  },
  { name: "الربيع", start: { month: 3, day: 21 }, end: { month: 6, day: 20 } },
  { name: "الصيف", start: { month: 6, day: 21 }, end: { month: 9, day: 22 } },
  {
    name: "الخريف",
    start: { month: 9, day: 23 },
    end: { month: 12, day: 20 },
  },
];

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_HIJRI_YEAR = Math.floor((CURRENT_YEAR - 622) * 1.03);

// Helper function to convert Hijri to Gregorian
const hijriToGregorian = (hijriDay, hijriMonth, hijriYear) => {
  // This is a simplified conversion. For production, use a proper library like hijri-converter
  const adjustment = Math.floor(hijriYear * 0.97); // Approximate adjustment factor
  const gregorianYear = hijriYear + 622 - adjustment;
  // Simplified month and day mapping (this is not accurate, just for demonstration)
  // In production, use a proper conversion library
  const monthDays = [30, 29, 30, 29, 30, 29, 30, 29, 30, 29, 30, 29]; // Hijri months
  let totalDays = 0;
  for (let i = 0; i < hijriMonth - 1; i++) {
    totalDays += monthDays[i];
  }
  totalDays += hijriDay - 1;
  // Convert to Gregorian days (approximate)
  const gregorianDays = Math.floor(totalDays * 1.0307);
  const date = new Date(gregorianYear, 0, 1);
  date.setDate(date.getDate() + gregorianDays);
  return date;
};

function AgeCalculatorGregorian() {
  const [convertedValue, setConvertedValue] = useState("gregorian");
  const [dateOfBirthGregorian, setDateOfBirthGregorian] = useState({
    day: "",
    month: "",
    year: ""
  });
  const [dateOfBirthHijri, setDateOfBirthHijri] = useState({
    day: "",
    month: "",
    year: ""
  });
  const [ageResult, setAgeResult] = useState(null);
  const [error, setError] = useState(null);
  const [hijriYears, setHijriYears] = useState([]);
  // Generate Hijri years list
  useEffect(() => {
    const years = [];
    for (let year = 1300; year <= CURRENT_HIJRI_YEAR + 10; year++) {
      years.push(year);
    }
    setHijriYears(years);
  }, []);

  const handleConversionValueChange = (newConversionType) => {
    setConvertedValue(newConversionType);
    setError(null);
    setDateOfBirthGregorian({ day: "", month: "", year: "" });
    setDateOfBirthHijri({ day: "", month: "", year: "" });
    setAgeResult(null);
  };
  const calculateAge = (birthDate, isHijri = false) => {
    const today = new Date();
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime()) || birth > today) {
      setError("يُرجى إدخال تاريخ ميلاد صحيح");
      setAgeResult(null);
      return;
    }
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();
    if (days < 0) {
      months--;
      const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    const hours = today.getHours() - birth.getHours();
    const minutes = today.getMinutes() - birth.getMinutes();
    const seconds = today.getSeconds() - birth.getSeconds();
    // Calculate total time units
    const totalDays = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
    const totalHours = Math.floor((today - birth) / (1000 * 60 * 60));
    const totalMinutes = Math.floor((today - birth) / (1000 * 60));
    const totalSeconds = Math.floor((today - birth) / 1000);
    const totalWeeks = Math.floor(totalDays / 7);
    const totalMonths = years * 12 + months;
    // Calculate next birthday
    let nextBirthday = new Date(
      today.getFullYear(),
      birth.getMonth(),
      birth.getDate()
    );
    if (nextBirthday < today) {
      nextBirthday.setFullYear(today.getFullYear() + 1);
    }
    const daysUntilBirthday = Math.ceil(
      (nextBirthday - today) / (1000 * 60 * 60 * 24)
    );
    // Get day of week born
    const dayOfWeekBorn = DAYS_OF_WEEK[birth.getDay()];
    // Get birth month in Arabic
    const birthMonthArabic = isHijri
      ? HIJRI_MONTHS[birth.getMonth()]
      : GREGORIAN_MONTHS[birth.getMonth()];
    // Get season born
    const birthMonth = birth.getMonth() + 1;
    const birthDay = birth.getDate();
    let seasonBorn = "";
    for (const season of SEASONS) {
      if (season.name === "الشتاء") {
        if (birthMonth === 12 || birthMonth <= 3) {
          if (
            (birthMonth === 12 && birthDay >= 21) ||
            birthMonth < 3 ||
            (birthMonth === 3 && birthDay <= 20)
          ) {
            seasonBorn = season.name;
            break;
          }
        }
      } else {
        const inStartRange =
          birthMonth > season.start.month ||
          (birthMonth === season.start.month && birthDay >= season.start.day);
        const inEndRange =
          birthMonth < season.end.month ||
          (birthMonth === season.end.month && birthDay <= season.end.day);
        if (inStartRange && inEndRange) {
          seasonBorn = season.name;
          break;
        }
      }
    }
    // Life milestone
    let lifeMilestone = "";
    if (years < 1) lifeMilestone = "رضيع";
    else if (years < 3) lifeMilestone = "طفل صغير";
    else if (years < 13) lifeMilestone = "طفل";
    else if (years < 20) lifeMilestone = "مراهق";
    else if (years < 40) lifeMilestone = "شاب";
    else if (years < 60) lifeMilestone = "منتصف العمر";
    else lifeMilestone = "كبير السن";
    setAgeResult({
      years,
      months,
      days,
      hours: Math.abs(hours),
      minutes: Math.abs(minutes),
      seconds: Math.abs(seconds),
      totalDays,
      totalHours,
      totalMinutes,
      totalSeconds,
      totalWeeks,
      totalMonths,
      daysUntilBirthday,
      dayOfWeekBorn,
      birthMonthArabic,
      seasonBorn,
      lifeMilestone,
      heartbeats: Math.floor(totalDays * 100000),
      breaths: Math.floor(totalDays * 20000),
      sleepHours: Math.floor(totalHours / 3),
      mealsEaten: Math.floor(totalDays * 3),
    });
    setError(null);
  };
  const handleCalculate = () => {
    if (convertedValue === "gregorian") {
      if (!dateOfBirthGregorian.day || !dateOfBirthGregorian.month || !dateOfBirthGregorian.year) {
        setError("يُرجى إدخال تاريخ الميلاد كاملاً");
        return;
      }
      // Validate day based on month
      const day = parseInt(dateOfBirthGregorian.day);
      const month = parseInt(dateOfBirthGregorian.month);
      const year = parseInt(dateOfBirthGregorian.year);
      // Check for valid date
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        setError("تاريخ الميلاد غير صحيح");
        return;
      }
      // Create date string in YYYY-MM-DD format
      const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calculateAge(dateString, false);
    } else {
      if (!dateOfBirthHijri.day || !dateOfBirthHijri.month || !dateOfBirthHijri.year) {
        setError("يُرجى إدخال تاريخ الميلاد كاملاً");
        return;
      }
      const hijriDay = parseInt(dateOfBirthHijri.day);
      const hijriMonth = parseInt(dateOfBirthHijri.month);
      const hijriYear = parseInt(dateOfBirthHijri.year);
      // Simple validation for Hijri date
      if (hijriDay < 1 || hijriDay > 30 || hijriMonth < 1 || hijriMonth > 12 || hijriYear < 1) {
        setError("تاريخ هجري غير صحيح");
        return;
      }
      // Convert Hijri to Gregorian for calculation
      const gregorianDate = hijriToGregorian(hijriDay, hijriMonth, hijriYear);
      calculateAge(gregorianDate, true);
    }
  };
  // Get days in month for Hijri
  const getHijriDaysInMonth = (month) => {
    // Hijri months have 29 or 30 days
    const longMonths = [1, 3, 5, 7, 9, 11]; // Odd months are usually 30 days
    if (longMonths.includes(parseInt(month))) {
      return 30;
    }
    return 29; // Even months are usually 29 days
  };
  return (
    <div style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة العمر الإلكترونية</strong> تمكنك من تحديد عمرك
                بدقة في التقويم الميلادي أو الهجري. تجيب هذه الأداة على
                تساؤلاتك مثل: "كم عمري بالضبط؟" و "كيف أحسب عمري؟" كل ما عليك
                هو إدخال تاريخ ميلادك باليوم والشهر والسنة، وستقوم الحاسبة
                بحساب عمرك بالسنوات والأشهر والأيام بدقة فائقة.
              </p>
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تأتي حاسبة العمر بميزة إضافية وهي <strong> حساب العمر بالدقائق </strong>
                والثواني. ستتمكن من معرفة عدد الدقائق التي قضيتها على هذا
                الكوكب بدقة تامة، بالإضافة إلى معلومات شيقة أخرى عن حياتك.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds /> <hr />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-3">
              <div className="p-3 mb-3 bg-success text-white rounded text-center">
                <h2 style={{ fontSize: "18px", margin: 0 }}>احسب عمرك الآن</h2>
              </div>
              <div className="row">
                <div
                  className="form-group d-flex align-items-center"
                  style={{
                    columnGap: "25px",
                    margin: "5px 15px 25px 0",
                  }}
                >
                  <label >
                    <strong>نوع التاريخ:</strong>
                  </label>
                  <div>
                    <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>ميلادي</span>
                      <input
                        type="radio"
                        name="calendarType"
                        value="gregorian"
                        onChange={() => handleConversionValueChange("gregorian")}
                        checked={convertedValue === "gregorian"}
                      />
                    </label>

                    <label style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                      <span>هجري</span>
                      <input
                        type="radio"
                        name="calendarType"
                        value="hijri"
                        onChange={() => handleConversionValueChange("hijri")}
                        checked={convertedValue === "hijri"}
                      />
                    </label>
                  </div>
                </div>
              </div>
              {convertedValue === "gregorian" ? (
                <div className="row">
                  <div className="col-md-12">
                    <label
                      htmlFor="dateOfBirth"

                      style={{ fontSize: "16px" }}
                    >
                      أدخل تاريخ ميلادك في التقويم الميلادي:
                    </label>
                    <div className="row">
                      <div className="col-md-4">
                        <label style={{ fontSize: "14px" }}>اليوم</label>
                        <select
                          className="form-control"
                          value={dateOfBirthGregorian.day || ""}
                          onChange={(e) => setDateOfBirthGregorian({ ...dateOfBirthGregorian, day: e.target.value })}
                          style={{ fontSize: "16px", padding: "10px" }}
                        >
                          <option value="">اليوم</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{day}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label style={{ fontSize: "14px" }}>الشهر</label>
                        <select
                          className="form-control"
                          value={dateOfBirthGregorian.month || ""}
                          onChange={(e) => setDateOfBirthGregorian({ ...dateOfBirthGregorian, month: e.target.value })}
                          style={{ fontSize: "16px", padding: "10px" }}
                        >
                          <option value="">الشهر</option>
                          {GREGORIAN_MONTHS.map((month, index) => (
                            <option key={index} value={index + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label style={{ fontSize: "14px" }}>السنة</label>
                        <select
                          className="form-control"
                          value={dateOfBirthGregorian.year || ""}
                          onChange={(e) => setDateOfBirthGregorian({ ...dateOfBirthGregorian, year: e.target.value })}
                          style={{ fontSize: "16px", padding: "10px" }}
                        >
                          <option value="">السنة</option>
                          {Array.from(
                            { length: CURRENT_YEAR - 1899 },
                            (_, i) => CURRENT_YEAR - i
                          ).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-12">
                    <label
                      htmlFor="hijriDatePicker"

                      style={{ fontSize: "16px" }}
                    >
                      أدخل تاريخ ميلادك في التقويم الهجري:
                    </label>
                    <div className="row">
                      <div className="col-md-4">
                        <label style={{ fontSize: "14px" }}>اليوم</label>
                        <select
                          className="form-control"
                          value={dateOfBirthHijri.day || ""}
                          onChange={(e) => {
                            setDateOfBirthHijri({ ...dateOfBirthHijri, day: e.target.value });
                          }}
                          style={{ fontSize: "16px", padding: "10px" }}
                        >
                          <option value="">اليوم</option>
                          {dateOfBirthHijri.month ?
                            Array.from(
                              { length: getHijriDaysInMonth(dateOfBirthHijri.month) },
                              (_, i) => i + 1
                            ).map(day => (
                              <option key={day} value={day}>{day}</option>
                            ))
                            :
                            Array.from({ length: 30 }, (_, i) => i + 1).map(day => (
                              <option key={day} value={day}>{day}</option>
                            ))
                          }
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label style={{ fontSize: "14px" }}>الشهر</label>
                        <select
                          className="form-control"
                          value={dateOfBirthHijri.month || ""}
                          onChange={(e) => {
                            setDateOfBirthHijri({ ...dateOfBirthHijri, month: e.target.value, day: "" });
                          }}
                          style={{ fontSize: "16px", padding: "10px" }}
                        >
                          <option value="">الشهر</option>
                          {HIJRI_MONTHS.map((month, index) => (
                            <option key={index} value={index + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label style={{ fontSize: "14px" }}>السنة</label>
                        <select
                          className="form-control"
                          value={dateOfBirthHijri.year || ""}
                          onChange={(e) => setDateOfBirthHijri({ ...dateOfBirthHijri, year: e.target.value })}
                          style={{ fontSize: "16px", padding: "10px" }}
                        >
                          <option value="">السنة</option>
                          {hijriYears.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <br />
              <div className="text-center mb-3 mt-4">
                <button
                  className="btn btn-success"
                  onClick={handleCalculate}
                >
                  احسب عمري
                </button>
              </div>
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}
              {ageResult && (
                <div
                  className="alert alert-success"
                  role="alert"
                  style={{ fontSize: "16px" }}
                >
                  <h3 style={{ fontSize: "20px", marginBottom: "15px" }}>
                    <strong>نتيجة حساب العمر:</strong>
                  </h3>
                  <div className="mb-3 p-3 bg-light rounded">
                    <strong style={{ fontSize: "18px" }}>عمرك هو:</strong>
                    <p style={{ fontSize: "17px", marginTop: "10px" }}>
                      {ageResult.years} سنة و {ageResult.months} شهر و{" "}
                      {ageResult.days} يوم
                    </p>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>عمرك بالتفصيل الدقيق</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          <li>✓ السنوات: {ageResult.years}</li>
                          <li>✓ الأشهر: {ageResult.months}</li>
                          <li>✓ الأيام: {ageResult.days}</li>
                          <li>✓ الساعات: {ageResult.hours}</li>
                          <li>✓ الدقائق: {ageResult.minutes}</li>
                          <li>✓ الثواني: {ageResult.seconds}</li>
                        </ul>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>إحصائيات إجمالية</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          <li>
                            📅 إجمالي الأيام المعاشة:{" "}
                            {ageResult.totalDays.toLocaleString()} يوم
                          </li>
                          <li>
                            📅 إجمالي الأسابيع:{" "}
                            {ageResult.totalWeeks.toLocaleString()} أسبوع
                          </li>
                          <li>
                            📅 إجمالي الأشهر:{" "}
                            {ageResult.totalMonths.toLocaleString()} شهر
                          </li>
                          <li>
                            ⏰ إجمالي الساعات:{" "}
                            {ageResult.totalHours.toLocaleString()} ساعة
                          </li>
                          <li>
                            ⏰ إجمالي الدقائق:{" "}
                            {ageResult.totalMinutes.toLocaleString()} دقيقة
                          </li>
                          <li>
                            ⏰ إجمالي الثواني:{" "}
                            {ageResult.totalSeconds.toLocaleString()} ثانية
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>معلومات شيقة عن حياتك</strong>
                      </div>
                      <div className="info-section-body">
                        <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                          معلومات عامة:
                        </h4>
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          <li>🗓️ ولدت يوم: {ageResult.dayOfWeekBorn}</li>
                          <li>📅 في شهر: {ageResult.birthMonthArabic}</li>
                          <li>🌸 في فصل: {ageResult.seasonBorn}</li>
                          <li>
                            🎂 الأيام المتبقية لعيد ميلادك القادم:{" "}
                            {ageResult.daysUntilBirthday} يوم
                          </li>
                          <li>👤 مرحلة الحياة: {ageResult.lifeMilestone}</li>
                        </ul>
                        <h4
                          style={{
                            fontSize: "16px",
                            marginTop: "15px",
                            marginBottom: "10px",
                          }}
                        >
                          حقائق مذهلة حصلت في حياتك (تقريباً):
                        </h4>
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          <li>
                            💓 نبض قلبك حوالي:{" "}
                            {ageResult.heartbeats.toLocaleString()} مرة
                          </li>
                          <li>
                            🫁 تنفست حوالي:{" "}
                            {ageResult.breaths.toLocaleString()} نفس
                          </li>
                          <li>
                            😴 أمضيت نائماً حوالي:{" "}
                            {ageResult.sleepHours.toLocaleString()} ساعة (
                            {Math.floor(ageResult.sleepHours / 24).toLocaleString()}{" "}
                            يوم)
                          </li>
                          <li>
                            🍽️ تناولت حوالي:{" "}
                            {ageResult.mealsEaten.toLocaleString()} وجبة
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  فهم أعمق للعمر بالتاريخ الميلادي واستخدام الحاسبات الدقيقة
                </strong>
              </div>
              <div className="info-section-body">
                <h2 style={{ fontSize: "18px", marginBottom: "15px" }}>
                  العمر بالتاريخ الميلادي: دليل شامل لحساب العمر بدقة
                </h2>
                <p style={{ lineHeight: "1.8" }}>
                  يُعتبر العمر مفهومًا هامًا في حياة الإنسان، ويمكن تقديره
                  بأساليب مختلفة تبعًا للثقافة والتقاليد. من ضمن هذه الأساليب،
                  نجد حساب العمر بالتاريخ الميلادي الذي يعتمد على التقويم
                  الميلادي. يتيح هذا النوع من الحسابات تحويل التاريخ إلى عمر
                  فعلي بدقة، وتقديم نتائج دقيقة تسهم في فهم أعمق للزمن الذي مر
                  به الشخص.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "20px" }}>
                  التقويم الميلادي وتاريخه
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  يعود استخدام التقويم الميلادي لحساب عمر الإنسان إلى التاريخ
                  الغربي والمسيحي. التقويم الميلادي يستند إلى تقويم
                  الغريغوريان، والذي تم تنصيبه في عام 1582 على يدي بابا
                  الفاتيكان غريغوريوس الثالث عشر. في التقويم الميلادي، يتكون
                  العام من 365 يومًا، باستثناء السنة الكبيسة التي تحتوي على يوم
                  إضافي في فبراير. تم اعتماد هذا التقويم لتوافقه مع دورة الأرض
                  حول الشمس بشكل أكثر دقة.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "20px" }}>
                  كيفية حساب العمر في التقويم الميلادي
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  عمر الشخص يُحسب بطرق مختلفة في ثقافات مختلفة. هذا الحاسب مبني
                  على النظام العمري الأكثر شيوعًا. في هذا النظام، يزيد العمر في
                  عيد ميلاد الشخص. على سبيل المثال، عمر الشخص الذي عاش لمدة 3
                  سنوات و11 شهرًا هو 3، وسيزيد عمره إلى 4 في عيد ميلاده التالي
                  بعد شهر. تستخدم معظم الدول الغربية هذا النظام العمري.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "20px" }}>
                  الأنظمة العمرية في الثقافات المختلفة
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  في بعض الثقافات، يُعبر عن العمر من خلال حساب السنوات مع أو
                  بدون تضمين السنة الحالية. في أحد أنظمة العمر التقليدية
                  الصينية، يولد الأشخاص وعمرهم 1 ويزداد عمرهم في رأس السنة
                  الصينية التقليدية بدلاً من عيد ميلادهم. على سبيل المثال، إذا
                  وُلِدَ طفل قبل يوم واحد فقط من رأس السنة الصينية التقليدية،
                  بعد يومين، سيكون عمر الطفل 2 على الرغم من أنه في سنته الثانية.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "20px" }}>
                  استخدام حاسبة العمر الاحترافية
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  استخدم حاسبة العمر الاحترافية هذه لتحويل التاريخ إلى العمر
                  بالتفصيل، مع مراعاة التقويم الميلادي. ستتمكن من تحويل تاريخ
                  ميلادك إلى العمر الفعلي بدقة، والاستفادة من الحسابات المعتمدة
                  على التقويم الميلادي. سواء كنت بحاجة لحساب العمر بالتاريخ
                  الميلادي، أو تحويل التاريخ إلى سنوات، فإن هذه الحاسبة الدقيقة
                  تقدم لك الحل الأمثل.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>حساب العمر إلكترونياً - السرعة والدقة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يرى بعض الناس أن العقل البشري قادر على معرفة العمر بشكل بديهي
                  أو شفوي. وبالرغم من أن العقل البشري يستطيع إجراء العمليات
                  الحسابية بدقة، إلا أن هذه العملية قد تأخذ وقتًا طويلاً. قد
                  تحتاج دقيقة واحدة أو أكثر، وذلك استنادًا إلى بديهية الشخص
                  ومهاراته في الرياضيات والحساب.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  مميزات الحساب الإلكتروني:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>السرعة:</strong> يوفر برنامج حساب العمر الإلكتروني
                    نتائج فورية في غضون بضع ثوانٍ
                  </li>
                  <li>
                    <strong>الدقة:</strong> حسابات دقيقة 100% مع مراعاة السنوات
                    الكبيسة والأشهر المختلفة
                  </li>
                  <li>
                    <strong>السهولة:</strong> لا يتطلب أي خبرة رياضية أو حسابية
                  </li>
                  <li>
                    <strong>التفصيل:</strong> يوفر معلومات شاملة بالسنوات
                    والأشهر والأيام والساعات والدقائق والثواني
                  </li>
                </ul>
                <p style={{ lineHeight: "1.8", marginTop: "15px" }}>
                  لذا، تُعتبر طريقة حساب العمر الإلكترونية أسهل وأسرع وأكثر دقة
                  من الطرق التقليدية للحساب الشفوي التي يقوم بها العقل البشري.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>حساب العمر بالأيام - حاسبة العمر بالأيام</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يعتمد نظامنا على إجراء اشتقاق دقيق لعدد الأيام بين يوم ميلادك
                  واليوم الحالي، باستخدام معادلات برمجية دقيقة ومدروسة بعناية
                  فائقة. تُطبق هذه المعادلات على البيانات التي تم إدخالها من
                  قبلك، وبالتالي يكون حساب العمر بالأيام متسقًا سواء كان ذلك في
                  التقويم الهجري أو الميلادي.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  لماذا حساب العمر بالأيام مهم؟
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>اليوم هو وحدة زمنية ثابتة في جميع التقويمات</li>
                  <li>
                    لا يتأثر بالفروقات بين التقويم الهجري والميلادي بنفس درجة
                    السنوات
                  </li>
                  <li>يوفر قياساً دقيقاً للعمر عند المقارنة بين التقويمين</li>
                  <li>
                    مفيد في الحسابات الطبية والعلمية التي تتطلب دقة عالية
                  </li>
                </ul>
                <p style={{ lineHeight: "1.8", marginTop: "15px" }}>
                  يعتبر اليوم وحدة قياس أساسية في أي تقويم، بغض النظر عن نوعه.
                  يتم احتساب الفرق بين التقاويم كل 33 سنة ميلادية تقريباً. ومع
                  تقدم العمر، يزداد هذا الفرق تدريجياً، ولكن يظل قياس العمر
                  بالأيام خاليًا من هذا الفرق والتباين بين التقاويم.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>كم عمري - احسب عمرك بدقة</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  سؤال يشغل الكثير من الأفكار يوميًا هو "كم عمري؟" ولذلك، نقدم
                  خدمة "احسب عمرك" التي تقدم إجابة دقيقة على هذا السؤال
                  بالتاريخ الميلادي. بالإضافة إلى ذلك، توفر الخدمة إمكانية معرفة
                  عمرك بالتقويم الهجري، والذي يتسم بالفارق الذي يبلغ حوالي أحد
                  عشر يومًا في كل سنة.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  فوائد حساب العمر المنتظم:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>البقاء على اطلاع دائم بعمرك الدقيق</li>
                  <li>تتبع المعالم العمرية المهمة</li>
                  <li>التخطيط للمستقبل بناءً على عمرك الحالي</li>
                  <li>معرفة الوقت المتبقي للمناسبات الهامة</li>
                  <li>مقارنة عمرك مع معدلات العمر المتوقع</li>
                </ul>
                <p style={{ lineHeight: "1.8", marginTop: "15px" }}>
                  من خلال استخدام تقنيات المعلومات وتوجيهها لهذه العمليات
                  الحسابية، أصبح من السهل حساب العمر بدقة وبسهولة، دون الحاجة
                  إلى التفكير البشري التقليدي الذي يستهلك وقتًا وجهدًا. كل ما
                  عليك هو ضبط تاريخ ميلادك، وستقوم الخدمة بتزويدك بالنتيجة
                  المطلوبة بشكل فوري ودقيق.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>كم باقي على عيد ميلادي؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  من بين المعلومات الشيقة جداً التي يقدمها لك نظامنا هي معرفة
                  الزمن المتبقي بالضبط على عيد ميلادك القادم. يتم ذلك عبر حساب
                  تاريخ الميلاد واشتقاق المدة المتبقية بتفصيل، حيث يتم عرضها
                  بالأيام بدقة تامة.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  أهمية معرفة الوقت المتبقي لعيد ميلادك:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>التخطيط المسبق للاحتفال بعيد ميلادك</li>
                  <li>تنظيم المفاجآت للأحباء في أعياد ميلادهم</li>
                  <li>الاستعداد النفسي لبداية عام جديد من عمرك</li>
                  <li>مشاركة العد التنازلي مع الأصدقاء والعائلة</li>
                  <li>إضافة المزيد من الإثارة والترقب للمناسبة</li>
                </ul>
                <p style={{ lineHeight: "1.8", marginTop: "15px" }}>
                  لا تنسى أيضًا الاستفادة من هذه الخدمة لمعرفة كم بقي من الوقت
                  لعيد ميلاد الأشخاص الذين تحبهم، لتشاركهم في هذه المناسبة
                  السعيدة وتزيدها إثارة ومتعة. يمكنك مشاركة موقعنا معهم لتضيف
                  لحظات سعيدة إلى حياتهم وتجعل احتفالاتهم أكثر تميزاً.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات حساب العمر في الحياة اليومية</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  الاستخدامات الشخصية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>معرفة عمرك الدقيق للأوراق الرسمية والمستندات</li>
                  <li>حساب عمر الأطفال لمتابعة نموهم وتطورهم</li>
                  <li>تحديد العمر المناسب للتسجيل في الأنشطة المختلفة</li>
                  <li>معرفة الفارق العمري بين الأشخاص</li>
                  <li>التخطيط للمناسبات والاحتفالات العائلية</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  الاستخدامات المهنية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>حساب سن التقاعد والتخطيط المالي</li>
                  <li>تحديد الأهلية للبرامج والخدمات الحكومية</li>
                  <li>التأمين الصحي وحساب الأقساط</li>
                  <li>التسجيل في البرامج التعليمية والدورات</li>
                  <li>الإجراءات القانونية التي تتطلب تحديد العمر الدقيق</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  الاستخدامات الطبية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>تحديد جداول التطعيم للأطفال</li>
                  <li>متابعة النمو والتطور حسب المراحل العمرية</li>
                  <li>حساب الجرعات الدوائية المناسبة</li>
                  <li>تقييم المخاطر الصحية المرتبطة بالعمر</li>
                  <li>التخطيط للفحوصات الطبية الدورية</li>
                </ul>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            أسئلة متكررة
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>ما هو العمر الميلادي؟</h3>
              </div>
              <div className="info-section-body">
                العمر الميلادي هو الطريقة التي نستخدمها لقياس عمر الشخص بناءً
                على تاريخ ميلاده وفقاً للتقويم الميلادي الشهري والسنوي. يعتمد
                التقويم الميلادي على دورة الأرض حول الشمس، وتتكون السنة
                الميلادية من 365 يوماً في السنة العادية و366 يوماً في السنة
                الكبيسة.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  كيف يمكنني حساب العمر الميلادي؟
                </h3>
              </div>
              <div className="info-section-body">
                يمكنك حساب العمر الميلادي بسهولة باستخدام حاسبتنا الإلكترونية.
                ابدأ بإدخال تاريخ ميلادك (اليوم والشهر والسنة)، ثم اضغط على زر
                "احسب عمري". ستقوم الحاسبة تلقائياً بحساب عمرك بالسنوات والأشهر
                والأيام والساعات والدقائق والثواني، مع مراعاة السنوات الكبيسة
                والأشهر ذات الأطوال المختلفة.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل هناك حاسبة لحساب العمر الميلادي؟
                </h3>
              </div>
              <div className="info-section-body">
                نعم، حاسبتنا الإلكترونية على هذه الصفحة تساعدك في حساب العمر
                الميلادي بدقة فائقة. يمكنك ببساطة إدخال تاريخ ميلادك في الحاسبة
                وستقوم بتقديم عمرك الدقيق بناءً على التقويم الميلادي، مع
                معلومات إضافية شيقة مثل يوم الأسبوع الذي ولدت فيه والأيام
                المتبقية لعيد ميلادك القادم.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما هي استخدامات حساب العمر الميلادي؟
                </h3>
              </div>
              <div className="info-section-body">
                حساب العمر الميلادي يُستخدم في العديد من السياقات المهمة مثل:
                <ul style={{ marginTop: "10px" }}>
                  <li>التأمل في تجربة الحياة الشخصية وتقييم الإنجازات</li>
                  <li>التخطيط للمستقبل وتحديد الأهداف الشخصية</li>
                  <li>التسجيل في الخدمات التعليمية والحكومية</li>
                  <li>مجالات التأمين والتقاعد والرعاية الصحية</li>
                  <li>متابعة نمو وتطور الأطفال</li>
                  <li>الإجراءات القانونية والرسمية</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل يمكن حساب العمر الميلادي يدويًا؟
                </h3>
              </div>
              <div className="info-section-body">
                نعم، يمكن حساب العمر الميلادي يدويًا باستخدام القوانين
                الميلادية، لكن العملية تتطلب وقتاً وجهداً كبيرين. ابدأ بتحديد
                عدد السنوات بين تاريخ الميلاد والتاريخ الحالي، ثم قم بطرح
                الأشهر والأيام مع مراعاة اختلاف أطوال الأشهر والسنوات الكبيسة.
                استخدام الحاسبة الإلكترونية أسرع وأدق بكثير، حيث تعطيك النتيجة
                في ثوانٍ معدودة مع ضمان الدقة الكاملة.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>كم باقي على عيد ميلادي؟</h3>
              </div>
              <div className="info-section-body">
                من بين المعلومات الشيقة التي يقدمها نظامنا معرفة الزمن المتبقي
                بالضبط على عيد ميلادك القادم. يتم حساب تاريخ الميلاد واشتقاق
                المدة المتبقية بالأيام بدقة تامة. إذا كان عيد ميلادك يصادف
                اليوم، يمكنك الاحتفال بهذا اليوم الخاص! هذه الخدمة مفيدة أيضاً
                لمعرفة الوقت المتبقي لأعياد ميلاد الأشخاص الذين تحبهم، لتشاركهم
                احتفالاتهم وتجعلها أكثر تميزاً.
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  ما الفرق بين حساب العمر بالميلادي والهجري؟
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>التقويم الميلادي:</strong>
                </p>
                <ul>
                  <li>يعتمد على دورة الشمس (365 يوم/366 في السنة الكبيسة)</li>
                  <li>يستخدم عالمياً في معظم الدول</li>
                  <li>الفصول ثابتة في نفس الأشهر كل عام</li>
                </ul>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>التقويم الهجري:</strong>
                </p>
                <ul>
                  <li>يعتمد على دورة القمر (354-355 يوم)</li>
                  <li>يستخدم في الدول الإسلامية</li>
                  <li>الفرق يزداد بمعدل سنة واحدة كل 33 سنة ميلادية</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  هل الحاسبة دقيقة في حساب العمر؟
                </h3>
              </div>
              <div className="info-section-body">
                نعم، حاسبتنا دقيقة جداً في حساب العمر. تستخدم الحاسبة معادلات
                برمجية متطورة تأخذ في الاعتبار جميع العوامل المؤثرة مثل:
                <ul style={{ marginTop: "10px" }}>
                  <li>السنوات الكبيسة (السنوات التي تحتوي على 366 يوماً)</li>
                  <li>
                    اختلاف أطوال الأشهر (بعضها 30 يوماً وبعضها 31 يوماً، وفبراير
                    28 أو 29)
                  </li>
                  <li>الحساب الدقيق للساعات والدقائق والثواني</li>
                  <li>فروقات التوقيت والمناطق الزمنية</li>
                </ul>
                هذا يضمن حصولك على نتائج دقيقة بنسبة 100%.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default AgeCalculatorGregorian;